import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  ChakraProvider,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import AdminHeader from '../components/AdminHeader';
import CaseStudyPreview from '../components/blog/CaseStudyPreview';
import { useAdminLeave } from '../components/AdminLeave';
import { deleteBlogImage, getBlog, imageSrc, saveBlog, uploadBlogImage } from '../blog/blogApi';
import {
  COURTS,
  LAW_CATEGORIES,
  PROVISION_GROUPS,
  SECTIONS,
  emptyFact,
  emptyForm,
  emptyIssue,
  emptyProvision,
  emptyRelatedCase,
  firstErrorSection,
  formFromBlog,
  formatTimestamp,
  moveItem,
  sectionHasError,
  toPayload,
  toPreviewModel,
  toSlug,
  validateBlog,
} from '../blog/blogForm';

const IMAGE_MAX_WIDTH = 1920;
const IMAGE_MAX_HEIGHT = 1080;
const IMAGE_LIMITS = 'Maximum allowed image size is 5 MB. Maximum resolution is 1920 × 1080 pixels. Recommended resolution is 1200 × 630 pixels. Supported image formats are JPG, PNG, WEBP, and GIF.';

function readImageResolution(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      const resolution = { width: image.naturalWidth, height: image.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(resolution);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read the image resolution.'));
    };
    image.src = url;
  });
}

const fieldProps = {
  borderRadius: 'md',
  border: '2px solid',
  borderColor: 'gray.200',
  bg: 'white',
  _hover: { borderColor: 'blue.300' },
  _focus: { borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' },
};

function Field({ label, required, hint, error, children }) {
  return (
    <FormControl isInvalid={Boolean(error)}>
      <FormLabel fontWeight="600" color="gray.700">
        {label}
        {required === true && (
          <Text as="span" color="red.500"> *</Text>
        )}
        {required === 'publish' && (
          <Text as="span" color="gray.400" fontWeight="500" fontSize="xs" ml={2}>
            Required to publish
          </Text>
        )}
      </FormLabel>
      {children}
      {hint && !error ? <FormHelperText>{hint}</FormHelperText> : null}
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
}

function RowActions({ index, total, onMove, onRemove }) {
  return (
    <HStack spacing={2} justify="flex-end">
      <Button type="button" size="xs" variant="ghost" onClick={() => onMove(index, -1)} isDisabled={index === 0}>
        Move up
      </Button>
      <Button type="button" size="xs" variant="ghost" onClick={() => onMove(index, 1)} isDisabled={index >= total - 1}>
        Move down
      </Button>
      <Button type="button" size="xs" variant="ghost" colorScheme="red" onClick={() => onRemove(index)}>
        Remove
      </Button>
    </HStack>
  );
}

function BlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { setGuard, confirmLeave } = useAdminLeave();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileRef = useRef(null);
  const snapshotRef = useRef(JSON.stringify(emptyForm()));
  const dirtyRef = useRef(false);
  const sessionUploads = useRef([]);
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ status: 'draft', updated_at: '', published_at: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [section, setSection] = useState('basic');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [loadError, setLoadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageResolution, setImageResolution] = useState(null);

  useEffect(() => {
    document.title = id ? 'Edit Blog' : 'Add New Blog';
  }, [id]);

  useEffect(() => {
    dirtyRef.current = JSON.stringify(form) !== snapshotRef.current;
  }, [form]);

  useEffect(() => {
    setGuard(() => {
      if (!dirtyRef.current) return true;
      return window.confirm('You have unsaved changes. Discard them and leave this page?');
    });
    return () => setGuard(null);
  }, [setGuard]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await getBlog(id);
        if (cancelled || !data.blog) return;
        const next = formFromBlog(data.blog);
        setForm(next);
        setMeta({
          status: data.blog.status || 'draft',
          updated_at: data.blog.updated_at || '',
          published_at: data.blog.published_at || '',
        });
        setSlugTouched(true);
        snapshotRef.current = JSON.stringify(next);
        dirtyRef.current = false;
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || 'Unable to load this blog post.');
          if (/authentication/i.test(error.message || '')) navigate('/Admin');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'title' && !slugTouched) next.slug = toSlug(value);
      if (name === 'slug') next.slug = toSlug(value);
      return next;
    });
    if (name === 'slug') setSlugTouched(true);
    clearError(name);
  };

  const updateList = (key, items) => {
    setForm((prev) => ({ ...prev, [key]: items }));
  };

  const updateProvision = (type, items) => {
    setForm((prev) => ({
      ...prev,
      provisions: { ...prev.provisions, [type]: items },
    }));
  };

  const leaveToList = () => {
    if (!confirmLeave()) return;
    navigate('/Admin/Blog');
  };

  const handleImage = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setErrors((prev) => ({ ...prev, featured_image: 'Upload a JPG, PNG, WEBP, or GIF image.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, featured_image: 'Image must be 5 MB or smaller.' }));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    let resolution;
    try {
      resolution = await readImageResolution(file);
    } catch (error) {
      setErrors((prev) => ({ ...prev, featured_image: 'Unable to read the image resolution.' }));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (resolution.width > IMAGE_MAX_WIDTH || resolution.height > IMAGE_MAX_HEIGHT) {
      setErrors((prev) => ({
        ...prev,
        featured_image: `Image resolution is ${resolution.width} × ${resolution.height} pixels. Maximum resolution is ${IMAGE_MAX_WIDTH} × ${IMAGE_MAX_HEIGHT} pixels.`,
      }));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setIsUploading(true);
    try {
      const previous = form.featured_image;
      const data = await uploadBlogImage(file);
      if (previous && sessionUploads.current.includes(previous)) {
        deleteBlogImage(previous).catch(() => {});
      }
      sessionUploads.current.push(data.path);
      setForm((prev) => ({
        ...prev,
        featured_image: data.path,
        featured_image_url: data.url,
      }));
      setImageResolution(resolution);
      clearError('featured_image');
    } catch (error) {
      setErrors((prev) => ({ ...prev, featured_image: error.message || 'Image upload failed.' }));
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = () => {
    const path = form.featured_image;
    if (path && sessionUploads.current.includes(path)) {
      deleteBlogImage(path).catch(() => {});
      sessionUploads.current = sessionUploads.current.filter((item) => item !== path);
    }
    setForm((prev) => ({ ...prev, featured_image: '', featured_image_url: '' }));
    setImageResolution(null);
  };

  const save = async (intent) => {
    const nextErrors = validateBlog(form, intent);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSection(firstErrorSection(nextErrors));
      toast({
        description: 'Please fix the highlighted fields.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom',
      });
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveBlog(toPayload(form, intent));
      const next = formFromBlog(result.blog);
      setForm(next);
      setMeta({
        status: result.blog.status || intent,
        updated_at: result.blog.updated_at || '',
        published_at: result.blog.published_at || '',
      });
      setSlugTouched(true);
      snapshotRef.current = JSON.stringify(next);
      dirtyRef.current = false;
      sessionUploads.current = [];
      setErrors({});
      toast({
        description: result.message || 'Blog post saved.',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom',
      });
      if (!id && result.blog?.id) {
        navigate(`/Admin/Blog/edit/${result.blog.id}`, { replace: true });
      }
    } catch (error) {
      if (error.fields) {
        setErrors(error.fields);
        setSection(firstErrorSection(error.fields));
      }
      toast({
        description: error.message || 'Unable to save this blog post.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      if (/authentication/i.test(error.message || '')) navigate('/Admin');
    } finally {
      setIsSaving(false);
    }
  };

  const sectionIndex = Math.max(0, SECTIONS.findIndex((item) => item.id === section));
  const activeSection = SECTIONS[sectionIndex];
  const previewImage = imageSrc(form);
  const isBusy = isSaving || isUploading;

  let sectionBody = null;
  if (section === 'basic') {
    sectionBody = (
      <VStack spacing={5} align="stretch">
        <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={4} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" px={4} py={3}>
          <Box>
            <FormLabel htmlFor="show-read-count" mb={0} fontWeight="600" color="gray.700">
              Show read count on the website
            </FormLabel>
            <FormHelperText mt={1}>
              {form.read_count === 1 ? '1 reader so far.' : `${form.read_count} readers so far.`} The count is always recorded. This switch only controls whether visitors can see it.
            </FormHelperText>
          </Box>
          <HStack spacing={2} flexShrink={0}>
            <Text fontSize="xs" fontWeight="700" color={form.show_read_count === true ? 'green.600' : 'gray.500'} w="32px">
              {form.show_read_count === true ? 'ON' : 'OFF'}
            </Text>
            <Switch
              id="show-read-count"
              colorScheme="green"
              isChecked={form.show_read_count === true}
              onChange={(event) => updateField('show_read_count', event.target.checked)}
            />
          </HStack>
        </FormControl>
        <Field label="Blog Title" required error={errors.title}>
          <Input {...fieldProps} value={form.title} placeholder="Enter the blog or case study title" onChange={(event) => updateField('title', event.target.value)} />
        </Field>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <Field label="Case Name" required error={errors.case_name}>
            <Input {...fieldProps} value={form.case_name} placeholder="e.g. Kesavananda Bharati v. State of Kerala" onChange={(event) => updateField('case_name', event.target.value)} />
          </Field>
          <Field label="Citation" required error={errors.citation}>
            <Input {...fieldProps} value={form.citation} placeholder="e.g. (1973) 4 SCC 225" onChange={(event) => updateField('citation', event.target.value)} />
          </Field>
          <Field label="Court" required error={errors.court}>
            <Input {...fieldProps} list="court-options" value={form.court} placeholder="e.g. Supreme Court of India" onChange={(event) => updateField('court', event.target.value)} />
            <datalist id="court-options">
              {COURTS.map((court) => <option key={court} value={court} />)}
            </datalist>
          </Field>
          <Field label="Category" required error={errors.category}>
            <Input {...fieldProps} list="category-options" value={form.category} placeholder="e.g. Constitutional Law" onChange={(event) => updateField('category', event.target.value)} />
            <datalist id="category-options">
              {LAW_CATEGORIES.map((item) => <option key={item} value={item} />)}
            </datalist>
          </Field>
          <Field label="Year of Decision" required error={errors.year_of_decision}>
            <Input {...fieldProps} type="number" min="1800" max="2100" value={form.year_of_decision} placeholder="e.g. 1973" onChange={(event) => updateField('year_of_decision', event.target.value)} />
          </Field>
          <Field label="Decision Date" required error={errors.decision_date}>
            <Input {...fieldProps} type="date" value={form.decision_date} onChange={(event) => updateField('decision_date', event.target.value)} />
          </Field>
        </SimpleGrid>
        <Field label="Short Summary" required error={errors.short_summary} hint="Shown on the blog card and near the top of the case study.">
          <Textarea {...fieldProps} rows={4} value={form.short_summary} placeholder="Summarise the case in a few sentences" onChange={(event) => updateField('short_summary', event.target.value)} />
        </Field>
        <Field label="Introduction" required error={errors.introduction}>
          <Textarea {...fieldProps} rows={6} value={form.introduction} placeholder="Introduce the case and why it is being discussed" onChange={(event) => updateField('introduction', event.target.value)} />
        </Field>
        <Field label="Brief Background" error={errors.brief_background}>
          <Textarea {...fieldProps} rows={6} value={form.brief_background} placeholder="Describe the background a reader needs before the facts" onChange={(event) => updateField('brief_background', event.target.value)} />
        </Field>
        <Field label="Importance of the Case" error={errors.importance}>
          <Textarea {...fieldProps} rows={5} value={form.importance} placeholder="Explain why this decision matters" onChange={(event) => updateField('importance', event.target.value)} />
        </Field>
      </VStack>
    );
  } else if (section === 'media') {
    sectionBody = (
      <VStack spacing={5} align="stretch">
        <Field label="Featured Image" error={errors.featured_image}>
          <Input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif" display="none" onChange={handleImage} />
          {previewImage && (
            <Image
              src={previewImage}
              alt={form.title || 'Featured image preview'}
              w="100%"
              maxH="240px"
              objectFit="cover"
              borderRadius="md"
              mb={3}
              onLoad={(event) => setImageResolution({
                width: event.target.naturalWidth,
                height: event.target.naturalHeight,
              })}
            />
          )}
          <HStack spacing={3}>
            <Button type="button" onClick={() => fileRef.current?.click()} isLoading={isUploading}>
              {form.featured_image ? 'Replace image' : 'Upload image'}
            </Button>
            {form.featured_image && (
              <Button type="button" variant="outline" colorScheme="red" onClick={removeImage}>
                Remove image
              </Button>
            )}
          </HStack>
          {imageResolution ? (
            <Text mt={3} fontSize="sm" color="gray.700" fontWeight="600">
              Current image resolution: {imageResolution.width} × {imageResolution.height} pixels.
            </Text>
          ) : null}
          <Text mt={2} fontSize="sm" color="gray.600">
            {IMAGE_LIMITS}
          </Text>
        </Field>
      </VStack>
    );
  } else if (section === 'facts') {
    sectionBody = (
      <VStack spacing={5} align="stretch">
        <Field label="Relevant Facts in Chronological Order" required error={errors.relevant_facts}>
          <Textarea {...fieldProps} rows={6} value={form.relevant_facts} placeholder="Narrate the facts in the order they happened" onChange={(event) => updateField('relevant_facts', event.target.value)} />
        </Field>
        <Field label="Parties Involved" required error={errors.parties_involved}>
          <Textarea {...fieldProps} rows={4} value={form.parties_involved} placeholder="Identify the petitioner, respondent, and other parties" onChange={(event) => updateField('parties_involved', event.target.value)} />
        </Field>
        <Field label="Events Leading to the Dispute" required error={errors.events_leading}>
          <Textarea {...fieldProps} rows={5} value={form.events_leading} placeholder="Describe what led the parties to court" onChange={(event) => updateField('events_leading', event.target.value)} />
        </Field>
        <Box>
          <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
            <Box>
              <Heading size="sm">Chronological Events</Heading>
              <Text color="gray.500" fontSize="sm" mt={1}>Optional. Add dated events when a timeline helps the reader.</Text>
              {errors.facts ? <Text color="red.500" fontSize="sm" mt={1}>{errors.facts}</Text> : null}
            </Box>
            <Button type="button" size="sm" onClick={() => updateList('facts', [...form.facts, emptyFact()])}>
              Add event
            </Button>
          </Flex>
          <VStack spacing={4} align="stretch">
            {form.facts.map((fact, index) => (
              <Box key={`fact-${index}`} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                <RowActions
                  index={index}
                  total={form.facts.length}
                  onMove={(itemIndex, direction) => updateList('facts', moveItem(form.facts, itemIndex, direction))}
                  onRemove={(itemIndex) => updateList('facts', form.facts.filter((_, item) => item !== itemIndex))}
                />
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={3}>
                  <Field label="Event date" error={errors[`fact_${index}_event_date`]}>
                    <Input {...fieldProps} type="date" value={fact.event_date} onChange={(event) => {
                      const next = form.facts.map((item, itemIndex) => itemIndex === index ? { ...item, event_date: event.target.value } : item);
                      updateList('facts', next);
                      clearError(`fact_${index}_event_date`);
                      clearError('facts');
                    }} />
                  </Field>
                  <Box gridColumn={{ md: 'span 2' }}>
                    <Field label="Description" error={errors[`fact_${index}_description`]}>
                      <Textarea {...fieldProps} rows={3} value={fact.description} placeholder="What happened on this date" onChange={(event) => {
                        const next = form.facts.map((item, itemIndex) => itemIndex === index ? { ...item, description: event.target.value } : item);
                        updateList('facts', next);
                        clearError(`fact_${index}_description`);
                        clearError('facts');
                      }} />
                    </Field>
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
            {form.facts.length === 0 && <Text color="gray.500" fontSize="sm">No events yet. Add one to build the timeline.</Text>}
          </VStack>
        </Box>
      </VStack>
    );
  } else if (section === 'issues') {
    sectionBody = (
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
          <Box>
            <Text color="gray.600">Add each question or issue before the court as its own entry. <Text as="span" color="red.500">*</Text></Text>
            {errors.legal_issues ? <Text color="red.500" fontSize="sm" mt={1}>{errors.legal_issues}</Text> : null}
          </Box>
          <Button type="button" size="sm" onClick={() => updateList('legal_issues', [...form.legal_issues, emptyIssue()])}>
            Add issue
          </Button>
        </Flex>
        {form.legal_issues.map((issue, index) => (
          <Box key={`issue-${index}`} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
            <RowActions
              index={index}
              total={form.legal_issues.length}
              onMove={(itemIndex, direction) => updateList('legal_issues', moveItem(form.legal_issues, itemIndex, direction))}
              onRemove={(itemIndex) => updateList('legal_issues', form.legal_issues.filter((_, item) => item !== itemIndex))}
            />
            <Field label={`Issue ${index + 1}`} required error={errors[`issue_${index}_issue_text`]}>
              <Textarea {...fieldProps} mt={3} rows={3} value={issue.issue_text} placeholder="State the legal question" onChange={(event) => {
                updateList('legal_issues', form.legal_issues.map((item, itemIndex) => itemIndex === index ? { issue_text: event.target.value } : item));
                clearError(`issue_${index}_issue_text`);
                clearError('legal_issues');
              }} />
            </Field>
          </Box>
        ))}
        {form.legal_issues.length === 0 && <Text color="gray.500" fontSize="sm">No legal issues yet.</Text>}
      </VStack>
    );
  } else if (section === 'provisions') {
    sectionBody = (
      <VStack spacing={8} align="stretch">
        {PROVISION_GROUPS.map((group) => (
          <Box key={group.type}>
            <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
              <Box>
                <Heading size="sm">
                  {group.label}
                  {group.type === 'statutory' ? <Text as="span" color="red.500"> *</Text> : null}
                </Heading>
                {group.type === 'statutory' && errors.provisions ? (
                  <Text color="red.500" fontSize="sm" mt={1}>{errors.provisions}</Text>
                ) : null}
              </Box>
              <Button type="button" size="sm" onClick={() => updateProvision(group.type, [...form.provisions[group.type], emptyProvision()])}>
                Add provision
              </Button>
            </Flex>
            <VStack spacing={3} align="stretch">
              {form.provisions[group.type].map((item, index) => (
                <Box key={`${group.type}-${index}`} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                  <RowActions
                    index={index}
                    total={form.provisions[group.type].length}
                    onMove={(itemIndex, direction) => updateProvision(group.type, moveItem(form.provisions[group.type], itemIndex, direction))}
                    onRemove={(itemIndex) => updateProvision(group.type, form.provisions[group.type].filter((_, item) => item !== itemIndex))}
                  />
                  <Field label={`Provision ${index + 1}`} required={group.type === 'statutory'} error={errors[`provision_${group.type}_${index}`]}>
                    <Textarea {...fieldProps} mt={3} rows={2} value={item.provision_text} placeholder={group.placeholder} onChange={(event) => {
                      updateProvision(group.type, form.provisions[group.type].map((row, rowIndex) => rowIndex === index ? { provision_text: event.target.value } : row));
                      clearError(`provision_${group.type}_${index}`);
                      if (group.type === 'statutory') clearError('provisions');
                    }} />
                  </Field>
                </Box>
              ))}
              {form.provisions[group.type].length === 0 && <Text color="gray.500" fontSize="sm">None added.</Text>}
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  } else if (section === 'arguments') {
    sectionBody = (
      <VStack spacing={5} align="stretch">
        <Field label="Arguments of the Petitioner / Appellant" error={errors.petitioner_arguments}>
          <Textarea {...fieldProps} rows={10} value={form.petitioner_arguments} placeholder="Record the submissions made for the petitioner or appellant" onChange={(event) => updateField('petitioner_arguments', event.target.value)} />
        </Field>
        <Field label="Arguments of the Respondent" error={errors.respondent_arguments}>
          <Textarea {...fieldProps} rows={10} value={form.respondent_arguments} placeholder="Record the submissions made for the respondent" onChange={(event) => updateField('respondent_arguments', event.target.value)} />
        </Field>
      </VStack>
    );
  } else if (section === 'judgment') {
    sectionBody = (
      <VStack spacing={5} align="stretch">
        {[
          ['final_decision', 'Final Decision of the Court', 'State what the court held', true],
          ['relief_granted', 'Relief Granted or Denied', 'Describe the relief and any directions', true],
          ['ratio_decidendi', 'Ratio Decidendi', 'State the principle that decides the case', false],
          ['judicial_reasoning', 'Judicial Reasoning / Analysis', 'Explain how the court analysed the dispute', false],
          ['interpretation_of_law', 'Interpretation of Law', 'Describe how the court read the applicable law', false],
          ['important_reasoning', 'Important Reasoning Used by the Judges', 'Capture reasoning that should be quoted or explained', false],
          ['precedents', 'Precedents Relied Upon or Distinguished', 'List the authorities and how they were used', false],
          ['impact', 'Impact of the Judgment', 'Explain the effect on later cases or practice', true],
          ['conclusion', 'Conclusion', 'Close the case study', true],
        ].map(([key, label, placeholder, required]) => (
          <Field key={key} label={label} required={required} error={errors[key]}>
            <Textarea {...fieldProps} rows={key === 'final_decision' || key === 'conclusion' ? 4 : 6} value={form[key]} placeholder={placeholder} onChange={(event) => updateField(key, event.target.value)} />
          </Field>
        ))}
      </VStack>
    );
  } else {
    sectionBody = (
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
          <Box>
            <Text color="gray.600">Optional. Add related decisions with a citation and source link when useful.</Text>
            {errors.related_cases ? <Text color="red.500" fontSize="sm" mt={1}>{errors.related_cases}</Text> : null}
          </Box>
          <Button type="button" size="sm" onClick={() => updateList('related_cases', [...form.related_cases, emptyRelatedCase()])}>
            Add related case
          </Button>
        </Flex>
        {form.related_cases.map((item, index) => (
          <Box key={`related-${index}`} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
            <RowActions
              index={index}
              total={form.related_cases.length}
              onMove={(itemIndex, direction) => updateList('related_cases', moveItem(form.related_cases, itemIndex, direction))}
              onRemove={(itemIndex) => updateList('related_cases', form.related_cases.filter((_, row) => row !== itemIndex))}
            />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={3}>
              <Field label="Case Name" error={errors[`related_${index}_case_name`]}>
                <Input {...fieldProps} value={item.case_name} placeholder="Related case name" onChange={(event) => {
                  updateList('related_cases', form.related_cases.map((row, rowIndex) => rowIndex === index ? { ...row, case_name: event.target.value } : row));
                  clearError(`related_${index}_case_name`);
                  clearError('related_cases');
                }} />
              </Field>
              <Field label="Citation" error={errors[`related_${index}_citation`]}>
                <Input {...fieldProps} value={item.citation} placeholder="e.g. AIR 1973 SC 1461" onChange={(event) => {
                  updateList('related_cases', form.related_cases.map((row, rowIndex) => rowIndex === index ? { ...row, citation: event.target.value } : row));
                }} />
              </Field>
              <Field label="Court" error={errors[`related_${index}_court`]}>
                <Input {...fieldProps} value={item.court} placeholder="Court" onChange={(event) => {
                  updateList('related_cases', form.related_cases.map((row, rowIndex) => rowIndex === index ? { ...row, court: event.target.value } : row));
                }} />
              </Field>
              <Field label="Year" error={errors[`related_${index}_year`]}>
                <Input {...fieldProps} type="number" value={item.year} placeholder="Year" onChange={(event) => {
                  updateList('related_cases', form.related_cases.map((row, rowIndex) => rowIndex === index ? { ...row, year: event.target.value } : row));
                  clearError(`related_${index}_year`);
                }} />
              </Field>
            </SimpleGrid>
            <Box mt={4}>
              <Field label="Reference URL" error={errors[`related_${index}_reference_url`]}>
                <Input {...fieldProps} value={item.reference_url} placeholder="https://" onChange={(event) => {
                  updateList('related_cases', form.related_cases.map((row, rowIndex) => rowIndex === index ? { ...row, reference_url: event.target.value } : row));
                  clearError(`related_${index}_reference_url`);
                }} />
              </Field>
            </Box>
          </Box>
        ))}
        {form.related_cases.length === 0 && <Text color="gray.500" fontSize="sm">No related cases yet.</Text>}
      </VStack>
    );
  }

  return (
    <ChakraProvider>
      <Box minH="100vh" bg="gray.50" pb={24}>
        <AdminHeader
          activeTab="blog"
          title={id ? 'Edit Blog' : 'Add New Blog'}
          badge={
            <Badge colorScheme={meta.status === 'published' ? 'green' : 'orange'}>
              {meta.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
          }
        />
        <Container maxW="7xl">
          {isLoading ? (
            <Center py={20}>
              <VStack>
                <Spinner color="blue.500" />
                <Text>Loading case study...</Text>
              </VStack>
            </Center>
          ) : loadError ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          ) : (
            <>
              <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
                <Box>
                  <Button type="button" variant="ghost" size="sm" onClick={leaveToList}>
                    Back to Blog Management
                  </Button>
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Fields marked with * are required before this case study can be saved.
                    {meta.updated_at ? ` Last saved ${formatTimestamp(meta.updated_at)}.` : ''}
                  </Text>
                </Box>
              </Flex>
              <Select
                display={{ base: 'block', lg: 'none' }}
                mb={4}
                value={section}
                onChange={(event) => setSection(event.target.value)}
                bg="white"
                aria-label="Form section"
              >
                {SECTIONS.map((item, index) => (
                  <option key={item.id} value={item.id}>
                    {index + 1}. {item.label}
                  </option>
                ))}
              </Select>
              <Flex align="flex-start" gap={6}>
                <Box display={{ base: 'none', lg: 'block' }} w="260px" flexShrink={0} position="sticky" top="4">
                  <VStack align="stretch" spacing={2}>
                    {SECTIONS.map((item, index) => (
                      <Button
                        key={item.id}
                        type="button"
                        justifyContent="flex-start"
                        variant={item.id === section ? 'solid' : 'ghost'}
                        colorScheme={sectionHasError(item.id, errors) ? 'red' : 'blue'}
                        onClick={() => setSection(item.id)}
                        whiteSpace="normal"
                        textAlign="left"
                        h="auto"
                        py={3}
                      >
                        {index + 1}. {item.label}
                      </Button>
                    ))}
                  </VStack>
                </Box>
                <Box flex="1" minW={0}>
                  <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
                    <CardBody p={{ base: 4, md: 6 }}>
                      <Text fontSize="sm" color="blue.600" fontWeight="700" mb={1}>
                        Section {sectionIndex + 1} of {SECTIONS.length}
                      </Text>
                      <Heading size="md" mb={1}>{activeSection.label}</Heading>
                      <Text color="gray.500" fontSize="sm" mb={6}>{activeSection.description}</Text>
                      {sectionBody}
                      <Flex justify="space-between" mt={8} gap={3} flexWrap="wrap">
                        <Button type="button" variant="outline" onClick={() => setSection(SECTIONS[sectionIndex - 1].id)} isDisabled={sectionIndex === 0}>
                          Previous section
                        </Button>
                        <Button type="button" colorScheme="blue" variant="outline" onClick={() => setSection(SECTIONS[sectionIndex + 1].id)} isDisabled={sectionIndex === SECTIONS.length - 1}>
                          Next section
                        </Button>
                      </Flex>
                    </CardBody>
                  </Card>
                </Box>
              </Flex>
            </>
          )}
        </Container>
        {!isLoading && !loadError && (
          <Box position="sticky" bottom={0} bg="white" borderTop="1px solid" borderColor="gray.200" py={3} zIndex={5} boxShadow="0 -4px 12px rgba(0,0,0,0.04)">
            <Container maxW="7xl">
              <Flex justify="flex-end" gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                <Button type="button" variant="ghost" onClick={leaveToList} isDisabled={isBusy}>Cancel</Button>
                <Button type="button" variant="outline" onClick={onOpen} isDisabled={isBusy}>Preview</Button>
                <Button type="button" onClick={() => save('draft')} isLoading={isSaving} isDisabled={isUploading}>Save as Draft</Button>
                <Button type="button" colorScheme="blue" onClick={() => save('published')} isLoading={isSaving} isDisabled={isUploading}>
                  {meta.status === 'published' ? 'Update Published' : 'Publish'}
                </Button>
              </Flex>
            </Container>
          </Box>
        )}
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Preview</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <CaseStudyPreview
                blog={toPreviewModel(form, meta.status)}
                notice="This preview uses the form as it is now, including unsaved edits. Drafts are not shown on the public website."
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </ChakraProvider>
  );
}

export default BlogEditor;
